import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import { LuInfo } from 'react-icons/lu'
import { StudentRecord } from 'components/StudentRecord'
import { Section } from 'components/ui/Section'
import { EmptyState, ErrorAlert, Skeleton } from 'components/ui/States'
import { useAuth } from 'hooks/useAuth'
import { getLast12MonthsRange, IDateRange, useExerciseRecords } from 'hooks/useExerciseRecords'
import { filterByRange, formatRange, summarize } from 'utils/stats'
import { DateRangeBar } from './components/DateRangeBar'
import { ExerciseSection } from './components/ExerciseSection'
import { ParticipationSection } from './components/ParticipationSection'
import { RangeSummary } from './components/RangeSummary'
import { RecordsTable } from './components/RecordsTable'
import { ThisMonthSection } from './components/ThisMonthSection'
import { TimeSection } from './components/TimeSection'

const DATE_INPUT_FORMAT = 'YYYY-MM-DD'
const QUICK_RANGE_DAYS = [30, 60]

const getLastDaysRange = (days: number) => ({
    from: dayjs().subtract(days, 'day').format(DATE_INPUT_FORMAT),
    to: dayjs().format(DATE_INPUT_FORMAT)
})

/** B. Page header: title, school name and data freshness. */
const PageHeader = ({ updatedAt }: { updatedAt: number }) => {
    const { me } = useAuth()
    const schoolName = me.data?.school_info?.name_zh
    return <header className='flex flex-col gap-1'>
        <p className='text-sm font-medium text-ink-muted'>智能運動場</p>
        <h1 className='text-h1 text-ink md:text-h1-md'>運動數據總覽</h1>
        <p className='text-sm text-ink-faint'>
            {schoolName ?? <span aria-hidden className='skeleton inline-block h-4 w-24 align-middle' />}
            {' · '}
            {updatedAt ? `更新於 ${dayjs(updatedAt).format('HH:mm')}` : '載入中…'}
        </p>
    </header>
}

/** Date range state (default: last 30 days). Empty input is ignored and from ≤ to is enforced. */
const useDateRangeState = () => {
    const [recordFrom, setRecordFrom] = useState(() => getLastDaysRange(30).from)
    const [recordTo, setRecordTo] = useState(() => getLastDaysRange(30).to)
    const recordRange = useMemo<IDateRange>(() => ({
        startDate: dayjs(recordFrom).startOf('day').toDate(),
        endDate: dayjs(recordTo).endOf('day').toDate()
    }), [recordFrom, recordTo])

    const selectLastDays = (days: number) => {
        const { from, to } = getLastDaysRange(days)
        setRecordFrom(from)
        setRecordTo(to)
    }

    const activeQuickDays = QUICK_RANGE_DAYS.find((days) => {
        const { from, to } = getLastDaysRange(days)
        return recordFrom === from && recordTo === to
    })

    const onChangeFrom = (value: string) => {
        if (!value || value > recordTo) return
        setRecordFrom(value)
    }

    const onChangeTo = (value: string) => {
        if (!value || value < recordFrom) return
        setRecordTo(value)
    }

    return { recordFrom, recordTo, recordRange, selectLastDays, activeQuickDays, onChangeFrom, onChangeTo }
}

export const DashboardPage = () => {
    const { recordFrom, recordTo, recordRange, selectLastDays, activeQuickDays, onChangeFrom, onChangeTo } = useDateRangeState()

    const { query, queryLast12Months } = useExerciseRecords(recordRange)
    const { data, isFetching, isFetchingNextPage, isPending: isRecordsPending, hasNextPage, fetchNextPage } = query
    const last12MonthsData = useMemo(() => queryLast12Months.data ?? [], [queryLast12Months.data])
    const isD12Loading = queryLast12Months.isPending
    const scrollAreaRef = useRef<HTMLDivElement>(null)
    const { records, recordTotal } = useMemo(() => ({
        records: data?.pages.flatMap((page) => page.data) ?? [],
        recordTotal: data?.pages?.[0]?.meta.pagination.total ?? 0,
    }), [data])

    // D12 window start; the range statistics only cover the overlap with it
    const windowStart = useMemo(() => getLast12MonthsRange().startDate, [])

    useEffect(() => {
        scrollAreaRef.current?.scrollTo({ top: 0 })
    }, [recordRange])

    const handleScroll = () => {
        const scrollArea = scrollAreaRef.current
        if (!scrollArea || isFetching || !hasNextPage) return

        const { scrollTop, scrollHeight, clientHeight } = scrollArea
        // Load the next page once we've scrolled to 80% of the scrollable height
        if (scrollTop + clientHeight >= scrollHeight * 0.8) {
            fetchNextPage()
        }
    }

    // R: D12 filtered to the selected range (DR pages are partial, never used for stats)
    const rangeData = useMemo(
        () => filterByRange(last12MonthsData, recordRange.startDate, recordRange.endDate),
        [last12MonthsData, recordRange]
    )
    const rangeSummary = useMemo(() => summarize(rangeData), [rangeData])
    const totalDays = dayjs(recordTo).diff(dayjs(recordFrom), 'day') + 1
    // Previous equal-length period, only when D12 fully covers it
    const previousSummary = useMemo(() => {
        const prevStart = dayjs(recordFrom).subtract(totalDays, 'day').startOf('day')
        const prevEnd = dayjs(recordFrom).subtract(1, 'day').endOf('day')
        if (prevStart.isBefore(dayjs(windowStart), 'day')) return undefined
        return summarize(filterByRange(last12MonthsData, prevStart.toDate(), prevEnd.toDate()))
    }, [last12MonthsData, recordFrom, totalDays, windowStart])
    const rangeLabel = formatRange(recordFrom, recordTo)
    const isPartialCoverage = dayjs(recordFrom).isBefore(dayjs(windowStart), 'day')
    const isRangeEmpty = !isD12Loading && rangeData.length === 0
    // The record queries only run once /users/me has loaded, so a failed `me` must surface too
    const { me } = useAuth()
    const hasError = me.isError || query.isError || queryLast12Months.isError

    const retry = () => {
        if (me.isError) me.refetch()
        if (query.isError) query.refetch()
        if (queryLast12Months.isError) queryLast12Months.refetch()
    }

    const switchTo60Days = activeQuickDays !== 60
        ? <button type='button' className='btn btn-secondary' onClick={() => selectLastDays(60)}>改為過去60日</button>
        : undefined

    return <main className='mx-auto w-full max-w-content space-y-6 px-4 py-6 sm:px-6 lg:space-y-8 lg:px-8 lg:py-8'>
        {/* B. Page header */}
        <PageHeader updatedAt={queryLast12Months.dataUpdatedAt} />

        {hasError && <ErrorAlert onRetry={retry} />}

        {/* C + D. This month and 12-month trend (D12) */}
        <ThisMonthSection data={last12MonthsData} loading={isD12Loading} windowStart={windowStart} />

        {/* E. Global date-range bar */}
        <DateRangeBar
            from={recordFrom}
            to={recordTo}
            quickDays={QUICK_RANGE_DAYS}
            activeDays={activeQuickDays}
            isFetching={isFetching && !isFetchingNextPage}
            onSelectDays={selectLastDays}
            onChangeFrom={onChangeFrom}
            onChangeTo={onChangeTo} />

        {isPartialCoverage && <div className='flex items-start gap-2 rounded-xl bg-warning-50 px-4 py-3 text-sm font-medium text-warning'>
            <LuInfo aria-hidden className='mt-0.5 h-4 w-4 shrink-0' />
            統計僅涵蓋過去12個月資料
        </div>}

        {/* F. Range summary */}
        <RangeSummary
            summary={rangeSummary}
            previous={previousSummary}
            totalDays={totalDays}
            recordTotal={isRecordsPending ? undefined : recordTotal}
            range={rangeLabel}
            loading={isD12Loading} />

        {/* G–I. Range breakdowns */}
        {isRangeEmpty
            ? <div className='card'>
                <EmptyState hint='試試調整日期範圍' action={switchTo60Days} />
            </div>
            : <>
                <ExerciseSection data={rangeData} range={rangeLabel} loading={isD12Loading} />
                <TimeSection data={rangeData} from={recordFrom} to={recordTo} range={rangeLabel} loading={isD12Loading} />
                <ParticipationSection data={rangeData} summary={rangeSummary} range={rangeLabel} loading={isD12Loading} />
            </>}

        {/* K. Student records (D12) */}
        <Section title='學生紀錄' subtitle='各年級、班級學生的運動總次數 · 過去12個月'>
            {isD12Loading
                ? <div className='space-y-3'>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className='h-4 w-full' />)}</div>
                : <StudentRecord data={last12MonthsData} />}
        </Section>

        {/* J. Records table (DR) */}
        <RecordsTable
            records={records}
            total={recordTotal}
            range={rangeLabel}
            scrollRef={scrollAreaRef}
            onScroll={handleScroll}
            isInitialLoading={isRecordsPending}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={!!hasNextPage}
            emptyAction={switchTo60Days} />
    </main>
}
